import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import React, { useLayoutEffect, useState, useEffect } from "react";
import styled from "styled-components";
import { useRef } from "react";

import { motion } from "framer-motion";

const Section = styled.section`
  min-height: 100vh;
  height: auto;
  width: 100vw;
  margin: 0 auto;
  overflow: hidden;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  position: relative;
  z-index: 10; // Ensure this section is above previous sections
`;

const Title = styled.h1`
  font-size: ${(props) => props.theme.fontxxxl};
  font-family: "Kaushan Script";
  font-weight: 300;
  text-shadow: 1px 1px 1px ${(props) => props.theme.body};
  color: ${(props) => props.theme.text};
  position: absolute;
  top: -4rem;
  left: 5%;
  z-index: 15;

  @media (max-width: 64em) {
    font-size: ${(props) => props.theme.fontxxl};
    top: -3rem;
  }
  @media (max-width: 48em) {
    font-size: ${(props) => props.theme.fontxl};
    top: -2rem;
  }
`;

const Left = styled.div`
  width: 35%;
  background-color: ${(props) => props.theme.body};
  color: ${(props) => props.theme.text};

  min-height: 100vh;
  z-index: 12;

  position: fixed;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  p {
    font-size: ${(props) => props.theme.fontlg};
    font-weight: 300;
    width: 80%;
    margin: 0 auto;
  }

  @media (max-width: 64em) {
    p {
      font-size: ${(props) => props.theme.fontmd};
    }
  }

  @media (max-width: 48em) {
    width: 40%;
    p {
      font-size: ${(props) => props.theme.fontsm};
    }
  }

  @media (max-width: 30em) {
    p {
      font-size: ${(props) => props.theme.fontxs};
    }
  }
`;

const Right = styled.div`
  position: absolute;
  left: 35%;
  padding-left: 30%;
  padding-right: 10%;
  min-height: 100vh;

  background-color: ${(props) => props.theme.grey};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  z-index: 11;

  h1 {
    width: 5rem;
    margin: 0 2rem;
  }
`;

const Item = styled(motion.div)`
  width: 20rem;
  margin-right: 6rem;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  img {
    width: 100%;
    height: 15rem;
    object-fit: cover;
    cursor: pointer;
    border-radius: 8px;
  }
  
  h1 {
    display: inline-block;
    width: fit-content;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    font-size: ${(props) => props.theme.fontmd};
    margin-top: 1rem;
  }
  
  p {
    font-size: ${(props) => props.theme.fontsm};
    text-align: center;
    margin-top: 0.5rem;
    color: ${(props) => props.theme.text};
    opacity: 0.8;
  }

  @media (max-width: 48em) {
    width: 15rem;
    
    img {
      height: 12rem;
    }
  }
`;

const ViewMoreButton = styled(motion.a)`
  width: 20rem;
  margin-right: 6rem;
  height: 15rem;
  
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  background-color: transparent;
  color: #000000;
  cursor: pointer;
  text-decoration: none;
  
  font-size: ${(props) => props.theme.fontlg};
  font-weight: 600;
  
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 48em) {
    width: 15rem;
    height: 12rem;
    font-size: ${(props) => props.theme.fontmd};
  }
`;

const LoadingText = styled.p`
  font-size: ${(props) => props.theme.fontlg};
  color: ${(props) => props.theme.text};
  text-align: center;
`;

const Project = ({ img, title = "", description = "" }) => {
  return (
    <Item
      initial={{ filter: "grayscale(100%)" }}
      whileInView={{ filter: "grayscale(0%)" }}
      transition={{ duration: 0.5 }}
      viewport={{ once: false, amount: "all" }}
    >
      <img src={img} alt={title} />
      <h1>{title}</h1>
      <p>{description}</p>
    </Item>
  );
};

const Projects = () => {
  gsap.registerPlugin(ScrollTrigger);

  const ref = useRef(null);
  const horizontalRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch projects from API
    fetch("https://portfolio.triodedevs.workers.dev/public/user/tri_m009")
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Sort by order and get top 4
          const topProjects = data.data
            .sort((a, b) => a.assignment.order - b.assignment.order)
            .slice(0, 4);
          setProjects(topProjects);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setLoading(false);
      });
  }, []);

  useLayoutEffect(() => {
    if (loading || projects.length === 0) return;

    let element = ref.current;
    let scrollingElement = horizontalRef.current;

    let pinWrapWidth = scrollingElement.offsetWidth;
    let t1 = gsap.timeline();

    setTimeout(() => {
      // Kill any existing ScrollTriggers for this element
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === element || trigger.trigger === scrollingElement) {
          trigger.kill();
        }
      });

      // Calculate the actual scroll distance needed
      // Account for the left panel (35% of viewport)
      let viewportWidth = window.innerWidth;
      let visibleWidth = viewportWidth * 0.65; // Only 65% is visible (right side)
      let scrollDistance = pinWrapWidth - visibleWidth;
      
      // Multiply by 3 for slower, smoother scrolling
      let scrollDuration = scrollDistance * 3;
      
      t1.to(element, {
        scrollTrigger: {
          trigger: element,
          start: "top top",
          end: `+=${scrollDuration}`,
          scroller: ".App",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
        height: `${scrollDuration}px`,
        ease: "none",
      });

      // Horizontal Scrolling
      t1.to(scrollingElement, {
        scrollTrigger: {
          trigger: scrollingElement,
          start: "top top",
          end: `+=${scrollDuration}`,
          scroller: ".App",
          scrub: 1,
        },
        x: -scrollDistance,
        ease: "none",
      });
      
      ScrollTrigger.refresh();
    }, 1000);

    // Refresh on window resize
    const handleResize = () => {
      ScrollTrigger.refresh();
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      t1.kill();
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === element || trigger.trigger === scrollingElement) {
          trigger.kill();
        }
      });
      window.removeEventListener('resize', handleResize);
    };
  }, [loading, projects]);

  return (
    <Section ref={ref} id="projects">
      <Title data-scroll data-scroll-speed="-1">
        Our Projects
      </Title>
      <Left>
        <p>
          We specialize in creating innovative digital solutions that transform ideas into reality. 
          Our portfolio showcases a diverse range of projects, from sleek websites and robust web applications 
          to cutting-edge IoT systems and automation solutions.
          <br />
          <br />
          Each project is crafted with attention to detail, user-friendly interfaces, and modern design principles. 
          We take pride in delivering high-quality work that meets our clients' unique needs and exceeds their expectations. 
          Explore our featured projects to see what we can create for you.
        </p>
      </Left>
      <Right ref={horizontalRef}>
        {loading ? (
          <LoadingText>Loading projects...</LoadingText>
        ) : (
          <>
            {projects.map((project) => (
              <Project
                key={project.id}
                img={project.media.images[0] || "https://via.placeholder.com/400x300"}
                title={project.title}
                description={project.short_description}
              />
            ))}
            <ViewMoreButton
              href="/projects"
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1, scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              View More Projects →
            </ViewMoreButton>
          </>
        )}
      </Right>
    </Section>
  );
};

export default Projects;